import type { ObjectCategory } from '@gmn-portal/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth';
import { resolveUserGroupKey } from '@/shared/utils/permissions';
import { payloadClient } from '.';
import type { FindPayloadApiClientParams, PaginatedDocs } from './types';

// Query keys for better type safety and reusability
export const objectCategoryKeys = {
  all: ['object-categories'] as const,
  lists: () => [...objectCategoryKeys.all, 'list'] as const,
  list: (params: ObjectCategoryQueryParams) => [...objectCategoryKeys.lists(), params] as const,
  sidebar: (params: SidebarObjectCategoryParamsKey) =>
    [...objectCategoryKeys.all, 'sidebar', params] as const,
  details: () => [...objectCategoryKeys.all, 'detail'] as const,
  detail: (id: string) => [...objectCategoryKeys.details(), id] as const,
} as const;

export type ObjectCategoryQueryParams = FindPayloadApiClientParams<
  'object-categories',
  ObjectCategory
> & {
  disabled?: boolean;
};

type SidebarObjectCategoryParams = {
  disabled?: boolean;
  limit?: number;
  sort?: ObjectCategoryQueryParams['sort'];
};

type SidebarObjectCategoryParamsKey = {
  groupKey?: string;
  userId?: string;
  sort?: ObjectCategoryQueryParams['sort'];
  limit?: number;
};

const buildEmptyCategoriesResponse = (limit: number): PaginatedDocs<ObjectCategory> => ({
  docs: [],
  hasNextPage: false,
  hasPrevPage: false,
  limit,
  nextPage: null,
  page: 1,
  pagingCounter: 0,
  prevPage: null,
  totalDocs: 0,
  totalPages: 0,
});

export const useObjectCategories = (params?: ObjectCategoryQueryParams) => {
  return useQuery({
    queryKey: objectCategoryKeys.list(params ?? {}),
    enabled: params?.disabled !== true,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await payloadClient.find({
        collection: 'object-categories',
        ...params,
      });
      return response;
    },
  });
};

export const useSidebarObjectCategories = (params?: SidebarObjectCategoryParams) => {
  const { user } = useAuth();
  const limit = params?.limit ?? 50;
  const sort = params?.sort ?? 'sortOrder';

  return useQuery<PaginatedDocs<ObjectCategory>>({
    queryKey: objectCategoryKeys.sidebar({
      groupKey: resolveUserGroupKey(user) ?? undefined,
      userId: user?.id ? String(user.id) : undefined,
      sort,
      limit,
    }),
    enabled: params?.disabled !== true && !!user,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      if (!user) {
        return buildEmptyCategoriesResponse(limit);
      }

      return payloadClient.customRequest<PaginatedDocs<ObjectCategory>>({
        subpath: '/object-categories/sidebar',
        method: 'POST',
        data: {
          limit,
          sort,
        },
      });
    },
  });
};

export const useObjectCategory = (id: string, params?: { disabled?: boolean }) => {
  return useQuery({
    queryKey: objectCategoryKeys.detail(id),
    enabled: !params?.disabled && !!id,
    queryFn: async () => {
      const response = await payloadClient.findById({
        collection: 'object-categories',
        id,
      });
      return response;
    },
  });
};

export const useCreateObjectCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<ObjectCategory>) => {
      const response = await payloadClient.create({
        collection: 'object-categories',
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.all });
    },
  });
};

export const useUpdateObjectCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ObjectCategory> }) => {
      const response = await payloadClient.updateById({
        collection: 'object-categories',
        id,
        data,
      });
      return response;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.detail(id) });
    },
  });
};

type ObjectCategoryOrderUpdate = {
  id: string;
  sortOrder: number;
};

export const useUpdateObjectCategoryOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: ObjectCategoryOrderUpdate[]) => {
      await Promise.all(
        updates.map((update) =>
          payloadClient.updateById({
            collection: 'object-categories',
            id: update.id,
            data: {
              sortOrder: update.sortOrder,
            },
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.all });
    },
  });
};

export const useDeleteObjectCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await payloadClient.deleteById({
        collection: 'object-categories',
        id,
      });
      return response;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.detail(id) });
    },
  });
};
